import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined';
import LockResetIcon from "@mui/icons-material/LockReset";
import { Box, IconButton, Tooltip } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { useSelector } from "react-redux";
import { StyledUsers } from "../../styled/users";
import ProviderInUser from "../../ui/users/providerInUser";
import { tableConfig } from "./config/tableConfig";
import TitleContainer from "./utilsTable/titleWithFetchData";

const UsersTable = ({
  data,
  loading,
  title,
  noRefresh,
  fetchData,
  handleOpenDelete,
  handleImpersonate,
  handleConfirmUser,
  handleOpenEdit,
  handleChangeMfa,
  handleUnlockUser,
  handleOpenChangePassword,
}) => {
  const esSoporte = useSelector(
    (state) => state.auth.roles?.tipo === "Soporte",
  );

  const columns = [
    {
      header: "Usuario",
      accessorKey: "email",
      filterFn: (row, columnId, value) => row.original.email.toLowerCase().includes(value.toLowerCase()),
    },
    {
      header: "Nombre completo",
      accessorKey: "nombreCompleto",
      accessorFn: (row) => {
        return `${row.apellido || ""}, ${row.nombre || ""}`;
      },
      sortingFn: (rowA, rowB, columnId) =>
        rowA.getValue(columnId).localeCompare(rowB.getValue(columnId), undefined, { sensitivity: 'base' }),
    },
    {
      header: "Rol",
      accessorKey: "roles",
      filterFn: (row, columnId, value) =>
        row.original.roles.some((rol) =>
          rol.toLowerCase().includes(value.toLowerCase()),
        ),
    },
    {
      accessorKey: "prestadores",
      header: "Prestador",
      enableColumnFilter: true,
      filterVariant: "text",
      filterFn: (row, columnId, value) => {
        return row.original.prestadores.some(
          (item) => item.nombre.toLowerCase().includes(value.toLowerCase())
        );
      },
      Cell: (rowData) => (
        <Box display="flex" flexWrap="wrap">
          {rowData.row.original.prestadores.length > 0 ? (
            rowData.row.original.prestadores.map((item) => (
              <StyledUsers.StyledChip
                key={item.id}
                label={item.nombre}
                disabled
              />
            ))
          ) : (
            <StyledUsers.EmptyText>
              Sin prestadores asociados
            </StyledUsers.EmptyText>
          )}
        </Box>
      ),
    },
    {
      accessorKey: "arts",
      header: "Art",
      Cell: (rowData) => (
        <Box>
          {rowData.row.original.arts.length > 0 ? (
            rowData.row.original.arts.map((art, index) => (
              <StyledUsers.ArtList key={index} component="span">
                {art.nombre}
                {index < rowData.row.original.arts.length - 1 && ", "}
              </StyledUsers.ArtList>
            ))
          ) : (
            <StyledUsers.EmptyText>Sin ART asociadas</StyledUsers.EmptyText>
          )}
        </Box>
      ),
      enableColumnFilter: true,
      filterVariant: "text",
      filterFn: (row, columnId, value) => {
        return row.original.arts.some(
          (art) => art.nombre.toLowerCase().includes(value.toLowerCase())
        );
      }
    },
    {
      accessorKey: "twoFactorEnabled",
      header: "MFA",
      Cell: (rowData) => (
        <Box>
          {rowData.row.original.twoFactorEnabled ? (
            <StyledUsers.StyledChip label="Activado" color="success" />
          ) : (
            <StyledUsers.StyledChip label="Desactivado" color="error" />
          )}
        </Box>
      ),
      accessorFn: (originalRow) => (originalRow.twoFactorEnabled ? "true" : "false"),
      filterVariant: "checkbox",
    }
  ];

  return (
    <StyledUsers.TableContainer>
      <StyledUsers.ScrollContainer>
        <MaterialReactTable
          {...tableConfig}
          columns={columns}
          data={data}
          state={{ isLoading: loading }}
          enableRowActions
          positionActionsColumn="last"
          renderRowActions={({ row }) => (
            <Box display="flex" gap={0.5}>
              {handleImpersonate && (
                <Tooltip title="Impersonar Usuario">
                  <span>
                    <IconButton
                      color="inherit"
                      onClick={() => {
                        const esPrestador =
                          row.original.roles.includes("PRESTADOR") &&
                          row.original.prestadores.length !== 0;
                        if (esPrestador) handleImpersonate(row.original);
                      }}
                      disabled={
                        !(
                          row.original.roles.includes("PRESTADOR") &&
                          row.original.prestadores.length !== 0
                        )
                      }>
                      <AccountCircleIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {handleConfirmUser && (
                <Tooltip title="Confirmar Usuario">
                  <IconButton
                    color="inherit"
                    onClick={() => handleConfirmUser(row.original)}
                  >
                    <CheckIcon />
                  </IconButton>
                </Tooltip>
              )}
              {handleOpenEdit && (
                <Tooltip title="Editar Usuario">
                  <IconButton
                    color="inherit"
                    onClick={() => handleOpenEdit(row.original)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
              {handleOpenChangePassword && (
                <Tooltip title="Resetear Contraseña">
                  <IconButton
                    color="inherit"
                    onClick={() => handleOpenChangePassword(row.original)}
                    disabled={esSoporte}
                  >
                    <LockResetIcon />
                  </IconButton>
                </Tooltip>
              )}
              {handleOpenDelete && (
                <Tooltip title="Eliminar Usuario">
                  <IconButton
                    color="inherit"
                    onClick={() => handleOpenDelete(row.original)}
                    disabled={esSoporte}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
              {handleChangeMfa && !esSoporte && (
                <Tooltip title="Habilitar/Desabilitar MFA">
                  <IconButton
                    onClick={() => handleChangeMfa(row.original)}
                  >
                    <LockPersonOutlinedIcon />
                  </IconButton>
                </Tooltip>
              )}
              {handleUnlockUser && (
                <Tooltip title="Desbloquear Usuario">
                  <IconButton
                    onClick={() => handleUnlockUser(row.original)}
                  >
                    <LockOpenIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          renderTopToolbarCustomActions={() => (
            <Box m={0.5}>
              <TitleContainer
                title={title}
                noRefresh={noRefresh}
                fetchData={fetchData}
              />
            </Box>
          )}
          renderDetailPanel={({ row }) => {
            const isPrestador = row.original.roles.includes("PRESTADOR");

            return isPrestador ? (
              <StyledUsers.ProviderContainer>
                <ProviderInUser user={row.original} refresh={fetchData} />
              </StyledUsers.ProviderContainer>
            ) : null;
          }}
        />
      </StyledUsers.ScrollContainer>
    </StyledUsers.TableContainer>
  );
};

export default UsersTable;
